import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ApiError } from '../api/client'
import {
  useAllocableCostCenters,
  useCostDrivers,
  useDriverAllocations,
  useReplaceDriverAllocations,
  useUpdateCostDriver,
} from '../api/hooks'
import type { CostCenter, DriverStatus } from '../api/types'
import { formatPercent } from '../components/Badges'

/**
 * Screen 4 — Static Driver Editor (§10).
 *
 * The cost center picker is sourced from the allocable set and shows the full tree
 * with any node selectable at any depth — parents included (D8). Where a driver targets
 * both a parent and its children the rows are indented so that is visible, and the
 * parent's row is labelled "direct", because it is legitimate but very easy to misread
 * as a subtotal.
 *
 * The running total is pinned to the footer and stays red until it hits exactly 100.
 */

/** Percentages are edited at 6 dp and displayed at 2 (§10). */
const TOLERANCE = 0.000001

interface Line {
  costCenterId: number
  percent: string
}

export function StaticDriverEditor() {
  const { id } = useParams<{ id: string }>()
  const driverId = Number(id)
  const navigate = useNavigate()

  const { data: drivers = [] } = useCostDrivers()
  const driver = drivers.find((d) => d.costDriverId === driverId)

  const { data: allocable = [] } = useAllocableCostCenters()
  const { data: allocations = [], isLoading } = useDriverAllocations(driverId)

  const [lines, setLines] = useState<Line[]>([])
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [loaded, setLoaded] = useState(false)

  const replace = useReplaceDriverAllocations()
  const update = useUpdateCostDriver()

  // Seed the editor from the current version once it arrives.
  useEffect(() => {
    if (loaded || isLoading) return

    const current = allocations.filter((a) => a.isCurrent)
    setLines(
      current.map((a) => ({ costCenterId: a.costCenterId, percent: String(a.percent) })),
    )
    setEffectiveFrom(
      current[0]?.effectiveFrom ?? new Date().toISOString().slice(0, 10),
    )
    setLoaded(true)
  }, [allocations, isLoading, loaded])

  const byId = useMemo(() => {
    const map = new Map<number, CostCenter>()
    for (const c of allocable) map.set(c.costCenterId, c)
    return map
  }, [allocable])

  const total = useMemo(
    () => lines.reduce((sum, l) => sum + (Number.parseFloat(l.percent) || 0), 0),
    [lines],
  )
  const balanced = Math.abs(total - 100) <= TOLERANCE

  /**
   * Rows ordered by tree position, so a parent and its children sit together and the
   * indentation reads as the hierarchy rather than as decoration.
   */
  const orderedLines = useMemo(() => {
    return [...lines].sort((a, b) => {
      const pa = byId.get(a.costCenterId)?.materializedPath ?? ''
      const pb = byId.get(b.costCenterId)?.materializedPath ?? ''
      return pa.localeCompare(pb)
    })
  }, [lines, byId])

  /** True when this line's cost center also has a descendant in the set. */
  function hasTargetedDescendant(costCenterId: number): boolean {
    const path = byId.get(costCenterId)?.materializedPath
    if (!path) return false
    return lines.some((l) => {
      if (l.costCenterId === costCenterId) return false
      const other = byId.get(l.costCenterId)?.materializedPath
      return other !== undefined && other.startsWith(path)
    })
  }

  function setPercent(costCenterId: number, value: string) {
    setLines((prev) =>
      prev.map((l) => (l.costCenterId === costCenterId ? { ...l, percent: value } : l)),
    )
    setSaved(false)
  }

  function addCostCenter(costCenterId: number) {
    if (lines.some((l) => l.costCenterId === costCenterId)) return
    setLines((prev) => [...prev, { costCenterId, percent: '0' }])
    setSaved(false)
  }

  function removeLine(costCenterId: number) {
    setLines((prev) => prev.filter((l) => l.costCenterId !== costCenterId))
    if (selectedId === costCenterId) setSelectedId(null)
    setSaved(false)
  }

  /** Split 100 evenly, giving the remainder to the first rows so the total is exact. */
  function distributeEvenly() {
    if (lines.length === 0) return

    const base = Math.floor((100 / lines.length) * 1_000_000) / 1_000_000
    const next = lines.map((l) => ({ ...l, percent: base.toFixed(6) }))
    const shortfall = 100 - base * lines.length

    if (shortfall > 0 && next[0]) {
      next[0] = { ...next[0], percent: (base + shortfall).toFixed(6) }
    }

    setLines(next)
    setSaved(false)
  }

  /** Give the selected row whatever is missing, so the set totals exactly 100. */
  function balanceToSelected() {
    if (selectedId === null) return

    const others = lines
      .filter((l) => l.costCenterId !== selectedId)
      .reduce((sum, l) => sum + (Number.parseFloat(l.percent) || 0), 0)

    setLines((prev) =>
      prev.map((l) =>
        l.costCenterId === selectedId ? { ...l, percent: (100 - others).toFixed(6) } : l,
      ),
    )
    setSaved(false)
  }

  function save() {
    setError(null)
    setSaved(false)

    replace.mutate(
      {
        id: driverId,
        effectiveFrom,
        lines: lines.map((l) => ({
          costCenterId: l.costCenterId,
          percent: Number.parseFloat(l.percent) || 0,
        })),
      },
      {
        onSuccess: () => setSaved(true),
        onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
      },
    )
  }

  function setStatus(status: DriverStatus) {
    if (!driver) return
    setError(null)
    update.mutate(
      {
        id: driverId,
        code: driver.code,
        name: driver.name,
        description: driver.description,
        status,
      },
      { onError: (err) => setError(err instanceof ApiError ? err.message : String(err)) },
    )
  }

  if (!driver) {
    return (
      <p className="p-4 text-[12px]" style={{ color: 'var(--color-muted)' }}>
        Loading driver…
      </p>
    )
  }

  const unusedCostCenters = allocable.filter(
    (c) => !lines.some((l) => l.costCenterId === c.costCenterId),
  )

  return (
    <div className="flex h-full">
      <section className="flex min-w-0 flex-1 flex-col">
        <div
          className="flex flex-wrap items-center gap-3 border-b px-4 py-2"
          style={{ background: 'var(--color-panel)', borderColor: 'var(--color-rule-soft)' }}
        >
          <button
            className="text-[12px] underline"
            style={{ color: 'var(--color-accent)' }}
            onClick={() => navigate('/drivers')}
          >
            ← Drivers
          </button>
          <h1 className="text-[14px] font-semibold">{driver.name}</h1>
          <span className="ident" style={{ color: 'var(--color-faint)' }}>
            {driver.code}
          </span>

          <select
            className="input ml-auto w-28"
            value={driver.status}
            onChange={(e) => setStatus(e.target.value as DriverStatus)}
          >
            <option value="Draft">Draft</option>
            <option value="Active">Active</option>
            <option value="Retired">Retired</option>
          </select>
        </div>

        {/*
          §10: an explicit warning when editing a driver used by a past run. The run is
          an immutable record of what posted, so it will not change — the definition and
          the run will simply disagree from here on, which is exactly what the run's
          stored snapshot exists to explain.
        */}
        {driver.usedInRunCount > 0 && (
          <div
            className="border-b px-4 py-2 text-[12px]"
            style={{
              background: 'var(--color-warn-wash)',
              color: 'var(--color-warn)',
              borderColor: 'var(--color-rule-soft)',
            }}
          >
            This driver was used by {driver.usedInRunCount} completed run
            {driver.usedInRunCount === 1 ? '' : 's'}. Saving a new version does not change what
            those runs posted — they keep their own snapshot of the percentages they used.
          </div>
        )}

        {error && (
          <div
            className="border-b px-4 py-2 text-[12px]"
            style={{
              background: 'var(--color-error-wash)',
              color: 'var(--color-error)',
              borderColor: 'var(--color-rule-soft)',
            }}
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto" style={{ background: 'var(--color-panel)' }}>
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10" style={{ background: 'var(--color-panel-alt)' }}>
              <tr className="border-b text-left" style={{ borderColor: 'var(--color-rule)' }}>
                <th className="col-head px-3 py-1.5">Cost center</th>
                <th className="col-head px-3 py-1.5">Code</th>
                <th className="col-head px-3 py-1.5 text-right">Share %</th>
                <th className="px-3 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {orderedLines.map((line) => {
                const cc = byId.get(line.costCenterId)
                const isDirectParent = hasTargetedDescendant(line.costCenterId)

                return (
                  <tr
                    key={line.costCenterId}
                    className="grid-row border-b"
                    style={{ borderColor: 'var(--color-rule-soft)' }}
                    data-selected={selectedId === line.costCenterId}
                    onClick={() => setSelectedId(line.costCenterId)}
                  >
                    <td className="px-3 py-1">
                      <span style={{ paddingLeft: `${(cc?.depth ?? 0) * 16}px` }}>
                        {cc?.name ?? `#${line.costCenterId}`}
                      </span>
                      {isDirectParent && (
                        <span
                          className="badge ml-2"
                          style={{
                            background: 'var(--color-accent-wash)',
                            color: 'var(--color-accent)',
                          }}
                          title="This cost center takes its own share alongside its children. The amount stays here and is never pushed down to them."
                        >
                          direct
                        </span>
                      )}
                    </td>
                    <td className="ident px-3 py-1" style={{ color: 'var(--color-muted)' }}>
                      {cc?.code}
                    </td>
                    <td className="px-3 py-1 text-right">
                      <input
                        className="input input-mono w-28 text-right"
                        value={line.percent}
                        onChange={(e) => setPercent(line.costCenterId, e.target.value)}
                        onFocus={() => setSelectedId(line.costCenterId)}
                        inputMode="decimal"
                      />
                    </td>
                    <td className="px-3 py-1 text-right">
                      <button
                        className="text-[11px] underline"
                        style={{ color: 'var(--color-muted)' }}
                        onClick={(e) => {
                          e.stopPropagation()
                          removeLine(line.costCenterId)
                        }}
                      >
                        remove
                      </button>
                    </td>
                  </tr>
                )
              })}

              {lines.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-4 text-[12px]" style={{ color: 'var(--color-muted)' }}>
                    No cost centers yet. Add them from the panel on the right.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* The running total, pinned. Red until it is exactly 100. */}
        <div
          className="flex shrink-0 items-center gap-4 border-t px-4 py-2"
          style={{
            background: balanced ? 'var(--color-ok-wash)' : 'var(--color-error-wash)',
            borderColor: 'var(--color-rule)',
            color: balanced ? 'var(--color-ok)' : 'var(--color-error)',
          }}
        >
          <span className="col-head" style={{ color: 'inherit' }}>
            total
          </span>
          <span className="num text-[15px] font-semibold">{formatPercent(total)}</span>
          {!balanced && (
            <span className="text-[12px]">
              {total < 100 ? 'short by' : 'over by'}{' '}
              <span className="num">{formatPercent(Math.abs(100 - total))}</span>
            </span>
          )}

          <div className="ml-auto flex items-center gap-2">
            <button className="btn btn-sm" onClick={distributeEvenly} disabled={lines.length === 0}>
              Distribute evenly
            </button>
            <button
              className="btn btn-sm"
              onClick={balanceToSelected}
              disabled={selectedId === null}
              title="Give the selected row whatever is missing so the set totals exactly 100."
            >
              Balance to selected
            </button>
          </div>
        </div>

        <div
          className="flex shrink-0 items-center gap-3 border-t px-4 py-2"
          style={{ background: 'var(--color-panel)', borderColor: 'var(--color-rule-soft)' }}
        >
          <label className="flex items-center gap-2 text-[12px]">
            <span className="col-head">effective from</span>
            <input
              type="date"
              className="input input-mono w-36"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
            />
          </label>

          <span className="text-[11px]" style={{ color: 'var(--color-faint)' }}>
            Saving closes the previous version the day before this date. Earlier periods keep
            resolving the definition they used.
          </span>

          <button
            className="btn btn-primary ml-auto"
            onClick={save}
            disabled={replace.isPending || !balanced || lines.length === 0}
            title={balanced ? undefined : 'The set must total exactly 100.'}
          >
            {replace.isPending ? 'Saving…' : 'Save version'}
          </button>
          {saved && (
            <span className="text-[12px]" style={{ color: 'var(--color-ok)' }}>
              Saved.
            </span>
          )}
        </div>
      </section>

      <aside
        className="flex w-72 shrink-0 flex-col border-l"
        style={{ background: 'var(--color-panel)', borderColor: 'var(--color-rule-soft)' }}
      >
        <div className="border-b px-3 py-2" style={{ borderColor: 'var(--color-rule-soft)' }}>
          <div className="col-head">add cost center</div>
          <p className="mt-1 text-[11px]" style={{ color: 'var(--color-faint)' }}>
            Any node at any depth. A parent may take its own share alongside its children.
          </p>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          {unusedCostCenters.map((cc) => (
            <button
              key={cc.costCenterId}
              className="grid-row flex w-full items-center gap-2 border-b px-3 py-1 text-left text-[12px]"
              style={{ borderColor: 'var(--color-rule-soft)' }}
              onClick={() => addCostCenter(cc.costCenterId)}
            >
              <span style={{ paddingLeft: `${cc.depth * 12}px` }}>{cc.name}</span>
              <span className="ident ml-auto" style={{ color: 'var(--color-faint)' }}>
                {cc.code}
              </span>
            </button>
          ))}
          {unusedCostCenters.length === 0 && (
            <p className="p-3 text-[11px]" style={{ color: 'var(--color-faint)' }}>
              Every allocable cost center is already in this driver.
            </p>
          )}
        </div>
      </aside>
    </div>
  )
}
